import { useEffect } from 'react';
import clsx from 'clsx';

import type { MinMaxType } from '@nf/types/common';
import BookingValueBloc from '../../blocs/booking-value-bloc';
import { useObservableState } from '@nf/utils-common/hooks';
import { reservationService } from '../../services/reservation.service';

import { MinusIcon, PlusIcon } from '@/components/icons/svg-icons';
import { BookingInputTip } from '../booking-input-tip';
import { bookingInputStyle } from './booking-input.style';

interface BookingInputProps {
	bloc: BookingValueBloc;
	title?: string;
	value: number;
	minMaxType: MinMaxType;
	betType: number;
	oddsType: number;
	priceType: number;
}

export const BookingInput = ({ 
	bloc, 
	title, 
	value, 
	minMaxType, 
	betType, 
	oddsType,
	priceType
}: BookingInputProps) => {
	const bookingValue = useObservableState(bloc.bookingValue$);
	const formattedValue = reservationService.formatBookingValue(
		bookingValue?.expectedValue ?? 0, 
		oddsType, 
		priceType, 
		betType,
	);
	const isPlusDisabled = Boolean(bookingValue?.isOddsTypeMaxValue);
	const isMinusDisabled = Boolean(bookingValue?.isOddsTypeMinValue);

	useEffect(() => {
		bloc.setBookingOptions({
			betType,
			oddsType,
			originalValue: value,
			minMaxType
		});

		const { min, max } = bloc.calculateDefaultIncrementedLimit(value);
		
		bloc.setInititalValue(minMaxType === 'min' ? min : max);
	}, [value, minMaxType, betType, oddsType]);

	const handleChange = () => {};

	const handleMinusClick = () => {
		bloc.minus();
	};

	const handlePlusClick = () => {
		bloc.plus();
	};

	const resetBookingError = () => {
		bloc.resetBookingError();
	};

	return (
		<div className="scheduled-betting__item">
			{title ? (
				<div className="scheduled-betting__label">
					{title}
				</div>
			) : null}

			<div className="scheduled-betting__input">
				<div className="input-range">
					{/* Minus button */}
					<div 
						className={clsx('input-range__lessen', bookingInputStyle.controlButton)}
						data-disable={isMinusDisabled}
						data-disabled={isMinusDisabled ? true : undefined}
						onClick={isMinusDisabled ? undefined : handleMinusClick}
					>
						<MinusIcon />
					</div>

					<div 
						className={clsx('stake-input stake-input--small', bookingInputStyle.input)}
						data-focus="false"
						data-selected="false"
						data-column="false"
					>
						<div className="stake-input__stake">
							<div className="stake-input__textfield">
								<div 
									className="stake-input__input" 
									data-show-placeholder="false" 
									data-placeholder=""
								>
									{formattedValue}
								</div>
								<input 
									className="input" 
									placeholder="" 
									type="number" 
									value={formattedValue}
									disabled
									onChange={handleChange}
								/>
							</div>
						</div>
					</div>

					{/* Plus button */}
					<div 
						className={clsx('input-range__add', bookingInputStyle.controlButton)}
						data-disable={isPlusDisabled}
						data-disabled={isPlusDisabled ? true : undefined}
						onClick={isPlusDisabled ? undefined : handlePlusClick}
					>
						<PlusIcon />
					</div>

					{bookingValue?.errorType ? (
						<BookingInputTip 
							errorType={bookingValue?.errorType}
							adjustedValue={formattedValue}
							onTipClose={resetBookingError}
						/>
					) : null}
				</div>
			</div>
		</div>
	);
};